import type { User } from "interface/user";

const CACHE_KEY = "tov-admin-permissions-v1";

type Cached = { userId: string; permissions: string[] };

export function getUserId(user: Pick<User, "id" | "_id">): string | undefined {
  return user.id ?? user._id;
}

export function writePermissionCache(user: User | null): void {
  if (!user) return;
  const userId = getUserId(user);
  if (!userId || !Array.isArray(user.permission)) return;
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ userId, permissions: user.permission }),
    );
  } catch {
    /* quota / private mode */
  }
}

export function readPermissionCache(userId: string): string[] | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Cached;
    if (parsed.userId !== userId || !Array.isArray(parsed.permissions)) {
      return undefined;
    }
    return parsed.permissions;
  } catch {
    return undefined;
  }
}

export function clearPermissionCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * When login/profile omits `permission`, reuse last known list for this user so
 * sub-admin menu checks work immediately (no wait for a second request).
 */
export function withPermissionsFromCache(user: User): User {
  const userId = getUserId(user);
  if (!userId) return user;
  if (Array.isArray(user.permission)) {
    return user;
  }
  const cached = readPermissionCache(userId);
  return { ...user, permission: cached ?? [] };
}
